import { useCallback } from 'react';
import $ from 'jquery';
import { isButtonDisabledByEligibility } from './formButtonState';

/**
 * useReturnLock
 *
 * Owns the three lock-related methods that were previously class methods on
 * FormViewer:
 *
 *   lockFieldsIfRequired()   — adds 'lockedField' CSS class + disables all
 *                              inputs when the return is locked
 *   toggleReturnStatusLock() — sends ReturnToggleStatusLock to calc server
 *   toggleFieldLock(bool)    — enables / disables all inputs and updates the
 *                              Redux lock flag
 *
 * Usage:
 *   const returnLock = useReturnLock({ calcAPIRef, isLocked, lockedStatus, spinner, toggleReturnLock, needingVerifiedSet });
 *   // Expose on socketCallbacksRef.current:
 *   lockFieldsIfRequired: () => returnLock.lockFieldsIfRequired(),
 *   toggleFieldLock:      v  => returnLock.toggleFieldLock(v),
 *   toggleReturnStatusLock: () => returnLock.toggleReturnStatusLock(),
 */
const useReturnLock = ({
  calcAPIRef,
  isLocked, // () => boolean  — class helper (reads lockedStatus prop)
  lockedStatus, // boolean prop
  spinner, // useSpinner result
  toggleReturnLock, // Redux action dispatcher (props.toggleReturnLock)
  needingVerifiedSet, // Redux action dispatcher (props.needingVerifiedSet)
}) => {
  /**
   * Applies 'lockedField' styling to every input in #taxform when the return
   * is currently locked.  Called after each form load.
   */
  const lockFieldsIfRequired = useCallback(() => {
    if (isLocked()) {
      const inputs = Array.from($('#taxform').find(':input'));
      inputs.forEach(x => {
        if (x.id && x.id !== '') {
          // NOTE: id MUST be string-quoted to successfully select IDs with dollar signs
          const obj = $(`[id="${x.id}"]`);
          obj.addClass('lockedField');
          x.disabled = true;
        }
      });
    }
  }, [isLocked]);

  /**
   * Sends the toggle-lock command to calc server and marks the return as
   * needing re-verification.
   */
  const toggleReturnStatusLock = useCallback(() => {
    const spinnerLockMsg = lockedStatus ? 'Unlocking Return...' : 'Locking Return...';
    spinner.startSpin(spinnerLockMsg);
    calcAPIRef.current.ReturnToggleStatusLock();
    needingVerifiedSet(true);
  }, [calcAPIRef, lockedStatus, needingVerifiedSet, spinner]);

  /**
   * Applies or removes the 'lockedField' CSS class from every input in
   * #taxform, then updates the Redux lock toggle.
   *
   * @param {boolean} fieldLock  true → lock fields, false → unlock fields
   */
  const toggleFieldLock = useCallback(
    fieldLock => {
      const inputs = Array.from($('#taxform').find(':input'));
      inputs.forEach(x => {
        if (x.id && x.id !== '') {
          const obj = $(`[id="${x.id}"]`);
          if (fieldLock) {
            // NOTE: id MUST be string-quoted to successfully select IDs with dollar signs
            obj.addClass('lockedField');
            x.disabled = true;
          } else {
            obj.removeClass('lockedField');
            x.disabled = isButtonDisabledByEligibility(x);
          }
        }
      });
      toggleReturnLock();
    },
    [toggleReturnLock],
  );

  return {
    lockFieldsIfRequired,
    toggleReturnStatusLock,
    toggleFieldLock,
  };
};

export default useReturnLock;
